/// <reference path="./react-table-config.d.ts" />
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React, { useEffect, useImperativeHandle, useRef } from 'react'
import {
    PluginHook,
    TableState,
    useColumnOrder,
    useExpanded,
    useFilters,
    useFlexLayout,
    useGroupBy,
    useResizeColumns,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table'
import { useVirtual } from 'react-virtual'

import SpecTableTitleSettings from './SpecTableTitleSettings'
import specTableStyle from './Spectable.styles'
import { SpecTableProps } from './Spectable.types'
import TableHeaderFilterRow from './TableHeaderFilterRow'
import actionColumn from './plugins/actionColumn'
import checkboxColumn from './plugins/checkboxColumn'
import groupedColumn from './plugins/groupedColumn'
import pinnedColumns from './plugins/pinnedColumns'
import useGroupingState from './plugins/useGroupingState'
import { TableSettingsProps } from './settings/TableSettings'
import TableBody from './tableBody/TableBody'

const defaultCheckboxColumnWidth = 48

export const SpecTable = <TData extends Object>({
    actionColumnProps,
    allowGrouping,
    bodyCellCssOverride,
    fetchDataLoading,
    fetchData,
    fetchNextDataLoading,
    height,
    onStateChange,
    showCheckboxColumn,
    tableOptions,
    specTableTitleSettings,
    totalItems,
    lazyLoading,
    isProjectHome,
    isImportTable,
    tableMinWidth,
    groupedCellHeight,
    childRef,
    hasNextPage,
    sortBy,
    showTVSettings,
    disableResizeColumns,
    inverseColumns,
    hideScroll,
    defaultSortCount,
    hideCheckbox,
    getDisabledCheckbox = () => false,
    checkboxDisabledTooltip = '',
    overscanRowCount = 10,
    LoadNextComponent,
    SkeletonComponent,
    enablePagination,
}: SpecTableProps<TData>) => {
    const classes = specTableStyle({
        lazyLoading: lazyLoading,
        isImportTable: isImportTable,
        tableMinWidth: tableMinWidth,
        groupedCellHeight: groupedCellHeight,
    })

    let plugins: PluginHook<TData>[] = [
        useFilters,
        useFlexLayout,
        useResizeColumns,
        useColumnOrder,
        useGroupBy,
        useSortBy,
        useExpanded,
        useRowSelect,
        (hooks) => {
            // plugin positioning is important here
            hooks.useControlledState.push(useGroupingState)
            hooks.visibleColumns.push(
                pinnedColumns({
                    checkboxColumnWidth: !showCheckboxColumn
                        ? 0
                        : defaultCheckboxColumnWidth,
                    showPinning:
                        specTableTitleSettings?.tableSettings?.showPinning ??
                        true,
                    pinningProps: specTableTitleSettings?.tableSettings
                        ?.initialPinningProps ?? {
                        firstColumnPinned: true,
                        lastColumnPinned: true,
                    },
                    actionColumnProps,
                })
            )
            !isImportTable &&
                hooks.visibleColumns.push(
                    groupedColumn(
                        specTableTitleSettings?.tableSettings
                            ?.initialPinningProps?.firstColumnPinned ?? true,
                        specTableTitleSettings?.tableSettings?.canToggleGroup ??
                            true,
                        isProjectHome,
                        tableOptions.groupColumnWidth
                    )
                )
            if (showCheckboxColumn)
                hooks.visibleColumns.push(
                    checkboxColumn({
                        defaultCheckboxColumnWidth,
                        hideCheckbox,
                        getDisabled: getDisabledCheckbox,
                        disabledTooltip: checkboxDisabledTooltip,
                    })
                )
            if (actionColumnProps) {
                hooks.visibleColumns.push(actionColumn(actionColumnProps))
            }
        },
    ]

    if (disableResizeColumns) {
        plugins.splice(2, 1)
    }

    const tableRef = useRef<HTMLDivElement>(null)
    const tableStateRef = useRef<TableState<TData>>()

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        columns,
        setHiddenColumns,
        setColumnOrder,
        toggleAllRowsExpanded,
        isAllRowsExpanded,
        state,
        visibleColumns,
        setAllFilters,
        toggleRowExpanded,
        toggleAllRowsSelected,
        setSortBy,
    } = useTable<TData>({ ...tableOptions }, ...plugins)

    const rowVirtualizer = useVirtual({
        size: rows.length,
        parentRef: tableRef,
        overscan: overscanRowCount,
    })

    const prevCount = usePrevious(rows.length)

    const items = rowVirtualizer.virtualItems
    if (rows.length < (prevCount ?? 0)) {
        if (items.length > 0) {
            // need to manually trigger scrollToIndex so the virtualizer will reset the scroll tracker
            rowVirtualizer.scrollToIndex(items[0].index ?? 0)
        }
    }

    const paddingTop = items.length > 0 ? items[0].start : 0
    const paddingBottom =
        items.length > 0
            ? rowVirtualizer.totalSize - items[items.length - 1].end
            : 0

    const tableSettings: TableSettingsProps<TData> = {
        ...specTableTitleSettings?.tableSettings,
        columns,
        allowGrouping,
        initialGroupByColumnIds: state.groupBy,
        initialHiddenColumnIds: state.hiddenColumns ?? [],
        setColumnOrder,
        setHiddenColumns,
        visibleColumns,
    }

    const computedWidth = '100%'

    const handleExpandAllClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        toggleAllRowsExpanded(true)
    }

    const handleCollapseAllClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        toggleAllRowsExpanded(false)
    }

    const handleToggleRowExpanded = (rows: Record<string, boolean>) => {
        for (const [key, value] of Object.entries(rows)) {
            toggleRowExpanded([key], value)
        }
    }

    const clearAllFilters = (_event: React.MouseEvent) => {
        setAllFilters([])
        fetchData()
    }

    useEffect(() => {
        if (!tableStateRef.current || !isEqual(tableStateRef.current, state)) {
            tableStateRef.current = state

            onStateChange?.(state)
        }
    }, [state, onStateChange])

    useEffect(() => {
        setSortBy(tableOptions.initialState?.sortBy ?? [])
    }, [tableOptions.initialState?.sortBy])

    useEffect(() => {
        const canToggleGroup =
            specTableTitleSettings?.tableSettings?.canToggleGroup ?? true
        // when group expand collpase is not allowed then group should be in expanded state.
        if (!canToggleGroup && !isAllRowsExpanded && totalItems > 0) {
            toggleAllRowsExpanded(true)
        }
    }, [
        state,
        specTableTitleSettings,
        toggleAllRowsExpanded,
        isAllRowsExpanded,
        totalItems,
    ])

    // Pass methods from useTable to the parent component table reference
    if (!!childRef) {
        useImperativeHandle(
            childRef,
            () => ({
                resetSelectedRows: () => toggleAllRowsSelected(false),
                handleToggleRowExpanded: handleToggleRowExpanded,
                setAllFilters,
            }),
            []
        )
    }

    return (
        <>
            {!!specTableTitleSettings && !!showTVSettings && (
                <SpecTableTitleSettings
                    {...{
                        hasGrouping: state.groupBy.length > 0,
                        onClearAllFilterClick: clearAllFilters,
                        onCollapseAllClick:
                            handleCollapseAllClick ??
                            toggleAllRowsExpanded(false),
                        onExpandAllClick:
                            handleExpandAllClick ?? toggleAllRowsExpanded(true),
                        ...specTableTitleSettings,
                        tableSettings,
                        width: computedWidth,
                    }}
                />
            )}
            <div
                {...getTableProps()}
                className={clsx(
                    classes.specTable,
                    'sticky',
                    !!hideScroll ? 'hideScroll' : '',
                    height === 'auto'
                        ? 'horz-scroll'
                        : isImportTable
                        ? 'vertical-scroll'
                        : 'full-scroll',
                    'table'
                )}
                style={{ width: computedWidth, height }}
                ref={!enablePagination ? tableRef : null}
                tabIndex={0}
            >
                <div className="header">
                    <TableHeaderFilterRow
                        cellCssOverride={bodyCellCssOverride}
                        checkboxColumnWidth={
                            !!showCheckboxColumn
                                ? defaultCheckboxColumnWidth
                                : 0
                        }
                        headerGroups={headerGroups}
                        showFilters={
                            specTableTitleSettings?.showFiltersRow ?? true
                        }
                        isImportTable={isImportTable}
                        clearAllFilters={clearAllFilters}
                        sortBy={sortBy}
                        defaultSortCount={defaultSortCount}
                        inverseColumns={inverseColumns}
                        hideCheckbox={hideCheckbox}
                    />
                </div>
                <div
                    {...getTableBodyProps()}
                    className={clsx(classes.tableBodyContainer, 'body')}
                >
                    {!fetchDataLoading &&
                    !fetchNextDataLoading &&
                    !hasNextPage &&
                    !rows.length ? (
                        <div
                            className={classes.centerContainer}
                            data-testid="noProjectContainer"
                        >
                            <Typography className={classes.grayText}>
                                No Results Found. Try Again.
                            </Typography>
                        </div>
                    ) : (
                        <TableBody
                            cellCssOverride={bodyCellCssOverride}
                            checkboxColumnWidth={
                                showCheckboxColumn
                                    ? defaultCheckboxColumnWidth
                                    : 0
                            }
                            prepareRow={prepareRow}
                            rows={rows}
                            paddingTop={paddingTop}
                            paddingBottom={paddingBottom}
                            rowVirtualizer={rowVirtualizer}
                            LoadNextComponent={LoadNextComponent}
                            SkeletonComponent={SkeletonComponent}
                            enablePagination={enablePagination}
                        />
                    )}
                </div>
            </div>
            <div className={classes.bottomMargin} />
        </>
    )
}
